/**
 * map.js
 */

import React from 'react';
import {
  Box,
} from '@mui/material';
import { MapContext } from './map-context';
import { TrackContext } from '../track-context';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { TRACK_TYPE } from '../tracking-common';
import MapTrack, { TRACK_COLOR } from './map-track';



export default function Map(props) {
  const { isDialog } = props;
  const { tracks } = React.useContext(TrackContext);
  const { trackingState } = React.useContext(LiveTrackingContext);
  const { 
    mapApi,
    baseContainer,
    setBaseContainer,
    map, 
    setMap, 
    mapType, 
    selectPoint, 
    deselectPoint,
    selectedPoint, 
    selectedMarkerUuid
  } = React.useContext(MapContext);
  const containerRef = React.useRef();
  const mapRef = React.useRef();
  const selectedPointRef = React.useRef();
  const markerClickListenerRef = React.useRef(null);
  const multiTrackColorMap = React.useRef({});


  // init or re-use map
  React.useEffect(() => {
    if (mapApi && mapType && mapRef.current && !map) {
      // initialize new map instance
      setBaseContainer(mapRef.current);
      setMap(new mapApi.Map(mapRef.current, {
        mapId: 'PackTrackTrackingMap',
        disableDefaultUI: true,
        mapTypeId: mapType,
      })); 
    }  else if (!isDialog && map && mapRef.current.children.length === 0) {
      // use existing map instance
      setBaseContainer(mapRef.current);
      const mapDiv = map.getDiv();
      mapRef.current.replaceChildren(mapDiv);
    }
  }, [mapApi, map, mapType, isDialog, setBaseContainer, setMap]);


  // handle map type changes
  React.useEffect(() => {
    if (map && mapType && map.mapTypeId !== mapType) {
      map.setMapTypeId(mapType);
    }
  }, [map, mapType]);


  // init click event handler for marker creation
  React.useEffect(() => {
    if (mapApi && map) {
      if (markerClickListenerRef.current) {
        mapApi.Event.removeListener(markerClickListenerRef.current);
        markerClickListenerRef.current = null;
      }

      markerClickListenerRef.current = map.addListener("click", (e) => {
        selectPoint(e);
      });

      return () => {
        mapApi.Event.removeListener(markerClickListenerRef.current);
      }
    }
  }, [mapApi, selectPoint, map, trackingState, tracks.length]);


  // create marker at selected point when it exists
  React.useEffect(() => {
    if (mapApi?.Marker && mapApi?.PinElement && map) {
      // remove existing
      if (selectedPointRef.current) {
        selectedPointRef.current.map = null;
      }

      // add new
      if (selectedPoint) {
        // TODO: put these colours somewhere central or and/or use an svg from the tracking API
        const pin = new mapApi.PinElement({
          background: '#c0c0c0',
          borderColor: '#77818e',
          glyphColor: '#77818e',
        });
        selectedPointRef.current = new mapApi.Marker({
          position: {
            lat: selectedPoint.latitude,
            lng: selectedPoint.longitude,
          },
          map,
          zIndex: 300,
          content: pin.element,
        });
      }
    }
  }, [mapApi, map, selectedPoint]);


  // if dialog map loads, use existing map.
  // on close, put it back in the original container.
  React.useEffect(() => {
    if (isDialog && map && mapRef.current) {
      const mapDiv = map.getDiv();
      if (!mapRef.current.contains(mapDiv)) {
        mapRef.current.replaceChildren(mapDiv);
      }
      return () => {
        const mapDiv = map.getDiv();
        if (!baseContainer.contains(mapDiv)) {
          baseContainer.replaceChildren(mapDiv);
        }
      }
    }
  }, [isDialog, map, baseContainer]);


  // memoized track rendering
  const renderedTracks = React.useMemo(() => {
    // track color helper
    const getTrackColor = (track) => {
      if (track.color) {
        return track.color;
      } else if (track.type !== TRACK_TYPE.MULTI_DEPLOYMENT) {
        return TRACK_COLOR[track.type];
      } else if (multiTrackColorMap.current[track.id]) {
        return multiTrackColorMap.current[track.id];
      } else {
        const usedColors = Object.values(multiTrackColorMap.current);
        const color = TRACK_COLOR[TRACK_TYPE.MULTI_DEPLOYMENT].find(color => !usedColors.includes(color));
        multiTrackColorMap.current[track.id] = color;
        return color;
      }
    }

    if (!tracks || tracks.length === 0) {
      return null;
    }

    // return any unused colors first
    const colorMapCopy = { ...multiTrackColorMap.current };
    tracks.forEach(track => {
      delete colorMapCopy[track.id];
    });
    for (let key in colorMapCopy) {
      delete multiTrackColorMap.current[key];
    }

    // render
    return tracks.map((track, i) => (
      <MapTrack
        {...track}
        key={track.id}
        color={getTrackColor(track)}
        isSelected={i === selectedPoint?.trackIdx || track.markers?.find((m) => m.uuid === selectedMarkerUuid)}
        index={i}
      />
    ));
  }, [selectedMarkerUuid, selectedPoint?.trackIdx, tracks]);


  // cleanup (clear selection)
  React.useEffect(() => {
    return () => {
      if (selectedPointRef.current) {
        selectedPointRef.current.map = null;
        deselectPoint();
      }
    }
  }, [mapApi, setMap, deselectPoint]);


  return (
    <>
      <Box sx={{ height: 1 }}>
        {/* map container container */}
        <Box 
          ref={containerRef}
          sx={{ height: 1, background: `repeating-linear-gradient(45deg, #b2b2b2 0px, #b2b2b2 15px, #aaa 15px, #aaa 30px);` }} 
          children={<Box sx={{ height: 1 }} ref={mapRef} />}
        />

        {renderedTracks}
      </Box>
    </>
  );
}